import { api } from "./api";
import { ListRequest } from "../types/common/commonTypes";
import {
  ItemCreate,
  ItemUpdate,
  ItemFull,
  ItemMin,
  PaginatedItemResponse,
} from "../types/itemTypes";

/**
 * Item service for managing item catalog
 *
 * Items use manual part numbers (no auto-generation)
 * Part numbers must be unique across the system
 *
 * API Endpoints: 8 total
 * - POST /api/v1/items/ (create)
 * - GET /api/v1/items/ (list with pagination)
 * - GET /api/v1/items/{item_id} (get by ID)
 * - PUT /api/v1/items/{item_id} (update)
 * - DELETE /api/v1/items/{item_id} (soft delete)
 * - GET /api/v1/items/part/{part_number} (get by part number)
 * - GET /api/v1/items/{item_id}/inventory (get inventory info)
 * - POST /api/v1/items/{item_id}/restore (restore deleted)
 */
const item_base = "/inventory/items";
export const itemService = {
  list: async (params: ListRequest): Promise<PaginatedItemResponse> => {
    const response = await api.post(`${item_base}/search`, params);
    return response.data;
  },
  listmin: async (): Promise<ItemMin[]> => {
    const response = await api.get(`${item_base}/min`);
    return response.data;
  },

  /**
   * Get item by ID
   * GET /api/v1/items/{item_id}
   */
  getById: async (id: string): Promise<ItemFull> => {
    const response = await api.get(`${item_base}/id/${id}`);
    return response.data;
  },

  /**
   * Get item by part number
   * GET /api/v1/items/part/{part_number}
   */
  getByPartNumber: async (partNumber: string): Promise<ItemFull> => {
    const response = await api.get(`${item_base}/number/${partNumber}`);
    return response.data;
  },

  /**
   * Create a new item
   * POST /api/v1/items/
   * Note: Part numbers are manually entered and must be unique
   */
  create: async (data: ItemCreate): Promise<ItemFull> => {
    const response = await api.post(`${item_base}/`, data);
    return response.data;
  },

  /**
   * Update an existing item
   * PUT /api/v1/items/{item_id}
   */
  update: async (id: string, data: ItemUpdate): Promise<ItemFull> => {
    const response = await api.put(`${item_base}/${id}`, data);
    return response.data;
  },

  /**
   * Delete an item (soft delete)
   * DELETE /api/v1/items/{item_id}
   */
  deactivate: async (id: string): Promise<void> => {
    await api.delete(`${item_base}/${id}`);
  },

  /**
   * Restore a deleted item
   * POST /api/v1/items/{item_id}/restore
   */
  reactivate: async (id: string): Promise<ItemFull> => {
    const response = await api.post(`${item_base}/${id}/restore`);
    return response.data;
  },
};
